<script lang="ts">
  import { onDestroy, tick, type Snippet } from "svelte"
  import Select from "svelte-select"

  type AsyncSelectRawOption = {
    name?: string
    label?: string
    value: string
    selectable?: boolean
    description?: string
  } & Record<string, unknown>

  type AsyncSelectItem = AsyncSelectRawOption & { label: string; name: string }

  type AsyncSelectChangeDetail = {
    value: string
    option: AsyncSelectItem | null
  }

  interface Props {
    value?: string
    selectedOption?: AsyncSelectRawOption | null
    id?: string
    classList?: string
    selectStyle?: string
    placeholder?: string
    ariaLabel?: string
    disabled?: boolean
    clearable?: boolean
    name?: string
    loadOptions: (input: string) => Promise<AsyncSelectRawOption[]>
    debounceWait?: number
    brightBackground?: boolean
    surfaceTone?: "auto" | "light" | "dark"
    fitSelectedText?: boolean
    loadingLabel?: string
    emptyLabel?: string
    onChange?: (event: CustomEvent<AsyncSelectChangeDetail>) => void
    onSelectionChange?: (option: AsyncSelectItem | null) => void
    selectionRenderer?: Snippet<[{ selection: AsyncSelectItem | null }]>
    itemRenderer?: Snippet<[{ item: AsyncSelectItem; disabled: boolean; active: boolean }]>
    dataInputId?: string
    dataTestid?: string
  }

  let {
    value = $bindable(""),
    selectedOption = $bindable<AsyncSelectRawOption | null>(null),
    id = "input-" + Math.random().toString(36).substring(2, 15),
    classList = "",
    selectStyle = "",
    placeholder = "Auswahl",
    ariaLabel = undefined,
    disabled = false,
    clearable = true,
    name = "",
    loadOptions,
    debounceWait = 300,
    brightBackground = false,
    surfaceTone = "auto",
    fitSelectedText = false,
    loadingLabel = "Suche läuft…",
    emptyLabel = "Keine Optionen gefunden",
    onChange = () => {},
    onSelectionChange = undefined,
    selectionRenderer = undefined,
    itemRenderer = undefined,
    dataInputId = id,
    dataTestid = undefined,
  }: Props = $props()

  const SELECT_PORTAL_ID = "kw-select-portal"
  const selectFloatingConfig = { strategy: "fixed" }
  const surfaceToneAttribute = $derived(surfaceTone === "auto" ? undefined : surfaceTone)
  const surfaceToneClass = $derived(
    surfaceTone === "auto" ? "" : ` svelte-select--surface-${surfaceTone}`,
  )
  const selectName = $derived((name || id) ?? id)

  let nativeSelect: HTMLInputElement | null = $state(null)
  let selectContainer: HTMLDivElement | null = $state(null)
  let listObserver: MutationObserver | null = null
  let items = $state<AsyncSelectItem[]>([])
  let selectBinding = $state<AsyncSelectItem | null>(null)
  let filterText = $state("")
  let loading = $state(false)
  let lastEmittedSelectionValue: string | null = null
  let resetLastEmittedSelectionValueScheduled = false

  const selectedFitLabel = $derived(
    selectBinding?.label ?? selectBinding?.name ?? selectBinding?.value ?? "",
  )
  const effectiveFitSelectedText = $derived(Boolean(fitSelectedText && selectedFitLabel))
  const resolvedSelectStyle = $derived(selectStyle.trim())

  const normalizeOption = (option: AsyncSelectRawOption): AsyncSelectItem => {
    const item = {
      ...option,
      label: option.label ?? option.name ?? option.value,
      name: option.label ?? option.name ?? option.value,
    }

    if (item.selectable === undefined) {
      delete item.selectable
    }

    return item
  }

  const normalizeOptions = (options: AsyncSelectRawOption[] | unknown): AsyncSelectItem[] =>
    Array.isArray(options) ? options.map(normalizeOption) : []

  const normalizeValue = (input: unknown) =>
    input === undefined || input === null ? "" : String(input)
  const isRawOption = (input: unknown): input is AsyncSelectRawOption =>
    input !== null &&
    typeof input === "object" &&
    typeof (input as { value?: unknown }).value === "string"
  const normalizeChangeOption = (detail: unknown): AsyncSelectItem | null => {
    if (!detail) return null

    if (Array.isArray(detail)) return detail.length ? normalizeChangeOption(detail[0]) : null

    if (typeof detail === "string") {
      const match = items.find((item) => item.value === detail)

      return match ?? normalizeOption({ value: detail })
    }

    if (typeof detail !== "object") return null

    const maybeWrapped = detail as { option?: unknown; value?: unknown }

    if (isRawOption(maybeWrapped.option)) return normalizeOption(maybeWrapped.option)

    if (isRawOption(detail)) return normalizeOption(detail)

    if (typeof maybeWrapped.value === "string") {
      const match = items.find((item) => item.value === maybeWrapped.value)

      return match ?? normalizeOption({ value: maybeWrapped.value })
    }

    return null
  }

  const ensureSelectPortalElement = () => {
    if (typeof document === "undefined") return null

    const existing = document.getElementById(SELECT_PORTAL_ID)

    if (existing) return existing

    const portal = document.createElement("div")
    portal.id = SELECT_PORTAL_ID
    portal.style.position = "fixed"
    portal.style.top = "0"
    portal.style.left = "0"
    portal.style.width = "100%"
    portal.style.height = "0"
    portal.style.zIndex = "12000"
    portal.style.pointerEvents = "none"
    document.body.appendChild(portal)

    return portal
  }

  const copySelectCssVariables = (source: HTMLElement, target: HTMLElement) => {
    if (typeof window === "undefined") return

    const styles = window.getComputedStyle(source)
    for (let index = 0; index < styles.length; index += 1) {
      const property = styles.item(index)

      if (property.startsWith("--")) {
        target.style.setProperty(property, styles.getPropertyValue(property))
      }
    }
    target.style.setProperty("--item-color", "var(--text-100)")
    target.style.setProperty("--item-hover-color", "var(--text-100)")
    target.style.setProperty("--item-is-active-color", "var(--text-100)")
    target.style.setProperty("--list-empty-color", "var(--text-muted, rgba(243, 238, 217, 0.72))")
  }

  const portalOpenSelectList = () => {
    if (!selectContainer) return

    const list = selectContainer.querySelector<HTMLElement>(":scope > .svelte-select-list")

    if (!list || list.dataset.kwSelectPortaled === "true") return

    // Native modal dialogs make body portals inert and paint above them.
    const portal = selectContainer.closest("dialog") ?? ensureSelectPortalElement()

    if (!portal) return

    copySelectCssVariables(selectContainer, list)
    const triggerWidth = Math.ceil(selectContainer.getBoundingClientRect().width)

    if (triggerWidth > 0) {
      list.style.minWidth = `${triggerWidth}px`
    }

    list.dataset.kwSelectPortaled = "true"
    list.classList.add("kw-select-list")
    list.style.pointerEvents = "auto"
    portal.appendChild(list)
  }

  const observeSelectList = (container: HTMLDivElement | null) => {
    listObserver?.disconnect()
    listObserver = null

    if (!container || typeof MutationObserver === "undefined") return

    listObserver = new MutationObserver(() => {
      void tick().then(portalOpenSelectList)
    })
    listObserver.observe(container, { childList: true })
    void tick().then(portalOpenSelectList)
  }

  const rememberEmittedSelectionValue = (nextValue: string) => {
    lastEmittedSelectionValue = nextValue

    if (resetLastEmittedSelectionValueScheduled) return

    resetLastEmittedSelectionValueScheduled = true
    queueMicrotask(() => {
      lastEmittedSelectionValue = null
      resetLastEmittedSelectionValueScheduled = false
    })
  }

  const fireChangeEvent = (option: AsyncSelectItem | null) => {
    const detail = { value, option }
    const changeEvent = new CustomEvent<AsyncSelectChangeDetail>("change", {
      detail,
      bubbles: true,
    })

    if (nativeSelect) {
      nativeSelect.value = value
      nativeSelect.dispatchEvent(changeEvent)

      return
    }

    onChange(changeEvent)
  }

  const handleNativeChange = (event: Event) => {
    onChange(event as CustomEvent<AsyncSelectChangeDetail>)
  }

  const runLoadOptions = async (input: string) => {
    loading = true
    try {
      const nextItems = normalizeOptions(await loadOptions(input))
      items = nextItems

      return nextItems
    } finally {
      loading = false
    }
  }

  const ensureInitialOptionsLoaded = () => {
    if (!items.length && !loading) void runLoadOptions("")
  }

  const preloadOptionsOnPointerOpen = (node: HTMLElement) => {
    node.addEventListener("pointerup", ensureInitialOptionsLoaded)

    return {
      destroy: () => node.removeEventListener("pointerup", ensureInitialOptionsLoaded),
    }
  }

  const handleSelectChange = async (event: CustomEvent<unknown>) => {
    let selection = normalizeChangeOption(event.detail)

    if (!selection) {
      await tick()
      selection = normalizeChangeOption(selectBinding)
    }

    if (!selection) return

    if (lastEmittedSelectionValue === selection.value) return

    selectBinding = selection
    selectedOption = selection
    value = normalizeValue(selection?.value)
    onSelectionChange?.(selection)
    fireChangeEvent(selection)
    rememberEmittedSelectionValue(selection.value)
  }

  const handleSelectClear = () => {
    if (disabled || !clearable) return

    selectBinding = null
    selectedOption = null
    value = ""
    onSelectionChange?.(null)
    fireChangeEvent(null)
    rememberEmittedSelectionValue("")
  }

  $effect(() => {
    observeSelectList(selectContainer)

    return () => {
      listObserver?.disconnect()
      listObserver = null
    }
  })

  $effect(() => {
    const normalizedValue = normalizeValue(value)

    if (!normalizedValue) {
      if (selectBinding !== null) selectBinding = null

      return
    }

    if (selectBinding?.value === normalizedValue) return

    const selected = selectedOption ? normalizeOption(selectedOption) : null

    if (selected?.value === normalizedValue) {
      selectBinding = selected

      if (!items.some((item) => item.value === selected.value)) {
        items = [selected, ...items]
      }
    }
  })

  onDestroy(() => {
    listObserver?.disconnect()
    listObserver = null
  })
</script>

<div
  class="select-input-shell"
  style={resolvedSelectStyle}
  data-testid={dataTestid}
  data-surface-tone={surfaceToneAttribute}
  data-fit-selected-text={effectiveFitSelectedText ? "true" : undefined}
  use:preloadOptionsOnPointerOpen
>
  <input
    type="hidden"
    bind:this={nativeSelect}
    id={dataInputId}
    data-input-id={dataInputId}
    data-testid={dataTestid}
    name={selectName}
    {value}
    aria-label={ariaLabel ?? placeholder}
    {disabled}
    onchange={handleNativeChange}
  />

  {#if effectiveFitSelectedText}
    <div class="select-fit-sizer" aria-hidden="true">
      <span class="select-fit-sizer__label">{selectedFitLabel}</span>
      <span class="select-fit-sizer__chrome"></span>
    </div>
  {/if}

  <Select
    class={`${classList ? `${classList} ` : ""}${effectiveFitSelectedText ? "kw-select-fit-selected-text " : ""}${brightBackground ? "svelte-select--bright" : "svelte-select--dark"}${surfaceToneClass}`}
    bind:value={selectBinding}
    bind:filterText
    bind:container={selectContainer}
    {disabled}
    {loading}
    {items}
    loadOptions={runLoadOptions}
    {id}
    placeholder=" "
    {clearable}
    {debounceWait}
    itemId="value"
    label="label"
    showChevron={true}
    closeListOnChange={true}
    floatingConfig={selectFloatingConfig}
    inputAttributes={{ autocomplete: "off", "aria-label": ariaLabel ?? placeholder }}
    on:focus={ensureInitialOptionsLoaded}
    on:change={handleSelectChange}
    on:select={handleSelectChange}
    on:clear={handleSelectClear}
  >
    <svelte:fragment slot="selection" let:selection>
      {#if selectionRenderer}
        {@render selectionRenderer({ selection })}
      {:else}
        <div class="select-selection">
          <span class="select-selection__label">{selection?.label ?? selection?.value ?? ""}</span>
        </div>
      {/if}
    </svelte:fragment>

    <svelte:fragment slot="item" let:item>
      {@const itemDisabled = item.selectable === false}
      {@const itemActive = selectBinding?.value === item.value}
      {#if itemRenderer}
        {@render itemRenderer({ item, disabled: itemDisabled, active: itemActive })}
      {:else}
        <div
          class="select-item"
          class:select-item--disabled={itemDisabled}
          class:select-item--active={itemActive}
        >
          <span class="select-item__label">{item.label ?? item.name ?? item.value}</span>
        </div>
      {/if}
    </svelte:fragment>

    <svelte:fragment slot="empty">
      <div class="async-select-empty">{loading ? loadingLabel : emptyLabel}</div>
    </svelte:fragment>
  </Select>
</div>

<style lang="less">
  :global(.svelte-select) {
    --item-color: var(--text-100);
    --item-hover-color: var(--text-100);
    --item-is-active-color: var(--text-100);
    --selected-item-color: var(--text-100);
  }

  :global(.svelte-select-list.kw-select-list) {
    color: var(--text-100);
    background: var(--list-background, var(--kontext-panel));
    z-index: 12000 !important;
  }

  .select-input-shell {
    width: 100%;
    --kw-select-fit-max-width: min(42rem, calc(100vw - 2rem));
    --kw-select-fit-chrome-width: 112px;
  }

  .select-input-shell[data-fit-selected-text="true"] {
    display: inline-grid;
    width: max-content;
    max-width: min(100%, var(--kw-select-fit-max-width));
  }

  .select-input-shell :global(.svelte-select) {
    width: 100%;
  }

  .select-input-shell :global(.svelte-select.kw-select-fit-selected-text) {
    width: 100%;
    max-width: var(--kw-select-fit-max-width);
  }

  .select-input-shell[data-fit-selected-text="true"]
    :global(.svelte-select.kw-select-fit-selected-text),
  .select-input-shell[data-fit-selected-text="true"] .select-fit-sizer {
    grid-area: 1 / 1;
    max-width: 100%;
  }

  .select-fit-sizer {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: max-content;
    max-width: var(--kw-select-fit-max-width);
    height: 0;
    overflow: hidden;
    visibility: hidden;
    pointer-events: none;
    white-space: nowrap;
    font-size: 1rem;
    line-height: 40px;
  }

  .select-fit-sizer__label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .select-fit-sizer__chrome {
    flex: 0 0 var(--kw-select-fit-chrome-width);
    width: var(--kw-select-fit-chrome-width);
  }

  .async-select-empty {
    padding: 0.75rem 1rem;
    color: var(--text-100);
    font-size: 0.92rem;
  }
</style>
